import {
  DndContext,
  type DragEndEvent,
  type DragMoveEvent,
  type DragStartEvent,
  useDraggable,
} from '@dnd-kit/core';
import classNames from 'classnames';
import {
  type CSSProperties,
  type PointerEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';

import { DEFAULT_GRID_COLUMNS } from '../PageConfigProvider/constants';
import { REPORT_FLOW_ROW_PX } from './convert-grid-report-to-flow';
import styles from './ReportFlow.module.css';
import {
  REPORT_FLOW_MAX_ITEM_HEIGHT_ROWS,
  REPORT_FLOW_MIN_ITEM_HEIGHT_ROWS,
  REPORT_FLOW_MIN_ITEM_WIDTH_COLS,
} from './report-flow-reducer';
import type { ReportFlowLeafBlock } from './report-flow-types';

type ResizeDirection = 'nw' | 'ne' | 'se' | 'sw';

export type ReportFlowResizeCommit = { width?: number; height?: number };

const CORNER_PATHS: Record<ResizeDirection, string> = {
  nw: 'M0.5 24.5V9.5C0.5 4.52944 4.52944 0.5 9.5 0.5H24.5',
  ne: 'M0.499999 0.499999L15.5 0.5C20.4706 0.5 24.5 4.52944 24.5 9.5L24.5 24.5',
  sw: 'M24.5 24.5L9.5 24.5C4.52944 24.5 0.500001 20.4706 0.500001 15.5L0.500003 0.499999',
  se: 'M 24.5 0.5 V 15.5 C 24.5 20.4706 20.4706 24.5 15.5 24.5 H 0.5',
};

const ResizeCorner = ({
  itemId,
  direction,
}: {
  itemId: string;
  direction: ResizeDirection;
}) => {
  const { attributes, listeners, setNodeRef } = useDraggable({
    id: `${itemId}-rf-resize-${direction}`,
    data: { direction },
  });

  return (
    <div
      ref={setNodeRef}
      data-direction={direction}
      className={styles.resizeCorner}
      {...attributes}
      {...listeners}
      onPointerDown={(event: PointerEvent) => {
        event.stopPropagation();
        listeners?.onPointerDown?.(event);
      }}
    >
      <svg
        width="25"
        height="25"
        viewBox="0 0 25 25"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d={CORNER_PATHS[direction]}
          stroke="currentColor"
          strokeLinecap="round"
        />
      </svg>
    </div>
  );
};

type StartSize = { widthPx: number; heightPx: number };
type PreviewSize = StartSize & { widthCols: number };

const clampWidthCols = (cols: number) =>
  Math.min(
    DEFAULT_GRID_COLUMNS,
    Math.max(REPORT_FLOW_MIN_ITEM_WIDTH_COLS, cols)
  );

const useLeafResize = ({
  leaf,
  allowHeight,
  widthColsForPx,
  onCommit,
}: {
  leaf: ReportFlowLeafBlock;
  allowHeight: boolean;
  widthColsForPx?: (widthPx: number, start: StartSize) => number;
  onCommit: (itemId: string, commit: ReportFlowResizeCommit) => void;
}) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [preview, setPreview] = useState<PreviewSize | null>(null);
  const startSizeRef = useRef<StartSize | null>(null);

  const widthCols = leaf.item.position.width;

  const computePreview = (
    start: StartSize,
    direction: ResizeDirection,
    deltaX: number,
    deltaY: number
  ): PreviewSize => {
    const pxPerCol = start.widthPx / widthCols;
    const widthDelta = direction.endsWith('w') ? -deltaX : deltaX;
    const heightDelta = direction.startsWith('n') ? -deltaY : deltaY;

    const widthPx = Math.min(
      DEFAULT_GRID_COLUMNS * pxPerCol,
      Math.max(
        REPORT_FLOW_MIN_ITEM_WIDTH_COLS * pxPerCol,
        start.widthPx + widthDelta
      )
    );
    const heightPx = allowHeight
      ? Math.min(
          REPORT_FLOW_MAX_ITEM_HEIGHT_ROWS * REPORT_FLOW_ROW_PX,
          Math.max(
            REPORT_FLOW_MIN_ITEM_HEIGHT_ROWS * REPORT_FLOW_ROW_PX,
            start.heightPx + heightDelta
          )
        )
      : start.heightPx;

    const nextWidthCols = clampWidthCols(
      widthColsForPx
        ? widthColsForPx(widthPx, start)
        : Math.round(widthPx / pxPerCol)
    );
    return { widthPx, heightPx, widthCols: nextWidthCols };
  };

  const handleDragStart = (_event: DragStartEvent) => {
    const element = wrapRef.current;
    if (!element) return;
    startSizeRef.current = {
      widthPx: element.offsetWidth,
      heightPx: element.offsetHeight,
    };
  };

  const handleDragMove = (event: DragMoveEvent) => {
    const direction = event.active.data.current?.direction as
      | ResizeDirection
      | undefined;
    const start = startSizeRef.current;
    if (!direction || !start) return;
    setPreview(computePreview(start, direction, event.delta.x, event.delta.y));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const direction = event.active.data.current?.direction as
      | ResizeDirection
      | undefined;
    const start = startSizeRef.current;
    startSizeRef.current = null;
    setPreview(null);
    if (!direction || !start) return;

    const next = computePreview(start, direction, event.delta.x, event.delta.y);

    const commit: ReportFlowResizeCommit = {};
    if (event.delta.x !== 0 && next.widthCols !== widthCols) {
      commit.width = next.widthCols;
    }
    if (allowHeight && event.delta.y !== 0) {
      const height = Math.round(next.heightPx / REPORT_FLOW_ROW_PX);
      if (height !== leaf.item.position.height) commit.height = height;
    }
    if (commit.width !== undefined || commit.height !== undefined) {
      onCommit(leaf.item.id, commit);
    }
  };

  const handleDragCancel = () => {
    startSizeRef.current = null;
    setPreview(null);
  };

  const corners = (
    <DndContext
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
      autoScroll={false}
    >
      <ResizeCorner itemId={leaf.item.id} direction="nw" />
      <ResizeCorner itemId={leaf.item.id} direction="ne" />
      <ResizeCorner itemId={leaf.item.id} direction="se" />
      <ResizeCorner itemId={leaf.item.id} direction="sw" />
    </DndContext>
  );

  return { wrapRef, preview, corners };
};

export const ReportFlowLeafFrame = ({
  leaf,
  disabled,
  onCommit,
  children,
}: {
  leaf: ReportFlowLeafBlock;
  disabled: boolean;
  onCommit: (itemId: string, commit: ReportFlowResizeCommit) => void;
  children: ReactNode;
}) => {
  const { wrapRef, preview, corners } = useLeafResize({
    leaf,
    allowHeight: true,
    onCommit,
  });

  const style = {
    '--rf-leaf-width':
      preview !== null ? `${preview.widthPx}px` : `${leaf.widthPct ?? 100}%`,
    ...(preview !== null && { '--rf-resize-h': `${preview.heightPx}px` }),
  } as CSSProperties;

  return (
    <div
      ref={wrapRef}
      className={classNames(
        styles.leafSizer,
        styles.leafFrame,
        preview !== null && styles.leafResizing
      )}
      data-align={leaf.align ?? 'left'}
      style={style}
      data-testid="report-flow-leaf-frame"
    >
      {children}
      {!disabled && corners}
    </div>
  );
};

export const ReportFlowCellResize = ({
  leaf,
  columnWidths,
  columnIndex,
  disabled,
  onCommit,
  children,
}: {
  leaf: ReportFlowLeafBlock;
  columnWidths: number[];
  columnIndex: number;
  disabled: boolean;
  onCommit: (itemId: string, commit: ReportFlowResizeCommit) => void;
  children: ReactNode;
}) => {
  const myFr = columnWidths[columnIndex] ?? leaf.item.position.width;
  const othersFr = columnWidths.reduce(
    (sum, width, index) => (index === columnIndex ? sum : sum + width),
    0
  );

  // Solve for the fr share whose resolved width tracks the pointer; shares rebalance as one grows.
  const widthColsForPx = (widthPx: number, start: StartSize) => {
    if (othersFr <= 0 || myFr <= 0) return myFr;
    const contentWidth = (start.widthPx * (myFr + othersFr)) / myFr;
    const target = Math.min(widthPx, contentWidth * 0.85);
    return Math.round((othersFr * target) / Math.max(contentWidth - target, 1));
  };

  const { wrapRef, preview, corners } = useLeafResize({
    leaf,
    allowHeight: leaf.kind !== 'text',
    widthColsForPx,
    onCommit,
  });

  const previewWidthCols = preview?.widthCols;
  useEffect(() => {
    if (previewWidthCols === undefined) return;
    const grid = wrapRef.current?.closest<HTMLElement>(
      '[data-testid="report-flow-columns-block"]'
    );
    if (!grid) return;
    const template = columnWidths
      .map((width, index) =>
        index === columnIndex ? `${previewWidthCols}fr` : `${width}fr`
      )
      .join(' ');
    grid.style.setProperty('--rf-columns-preview', template);
    return () => {
      grid.style.removeProperty('--rf-columns-preview');
    };
  }, [previewWidthCols, columnWidths, columnIndex, wrapRef]);

  const style =
    preview !== null && leaf.kind !== 'text'
      ? ({ '--rf-resize-h': `${preview.heightPx}px` } as CSSProperties)
      : undefined;

  return (
    <div
      ref={wrapRef}
      className={classNames(
        styles.cellResizeWrap,
        preview !== null && styles.leafResizing
      )}
      style={style}
    >
      {children}
      {!disabled && corners}
    </div>
  );
};
